/* CALIBRATE CAMERA — moving the viewer from On-site.

   On-site is where a calibration is judged: you look at the composite and see
   whether the illusion sits right on the building. But the viewpoint itself
   could only be moved somewhere else — by dragging the eye helper in Orbit, or
   by typing metres into Master Settings — so the loop was "nudge, switch view,
   look, switch back". This closes it.

   It drives S.nudge, the deliberate offset from the solved eye (see its note in
   state/store.js), not the solve itself. Nothing here invalidates the
   calibration: the four corners still define the viewpoint, and this says "and
   a bit further out than that".

   Deliberately a fixed widget rather than a gizmo in the scene. The viewer has
   no position ON the photo — it is where the camera stood, outside the picture
   — so there is nowhere in the composite for a handle to live. A corner of the
   view, with three arrows big enough to hit without aiming, is the honest
   shape for it. */
import * as THREE from 'three';
import { emit, on, requestRender } from '../state/bus.js';
import { pushHistory } from '../state/history.js';
import { $, S, el } from '../state/store.js';
import { cameraObj, eyeVec, rebuildHelpers } from '../scene/helpers.js';
import { syncTransform } from '../scene/objects.js';
import { GIZMO_COLOURS, moveArrowMeshes, moveHubMesh } from '../scene/gizmo.js';

/* Where the three axes MEET, in viewBox units. Not a constant any more:
   relayout() below works it out from how much room the labels actually take,
   so the finished drawing sits in the middle of the panel rather than the
   origin sitting in the middle and the drawing hanging off it. */
let C = { x: 50, y: 56 };

/* THE ARROWS ARE THE GIZMO'S OWN ARROWS, rendered.

   They were flat SVG lookalikes, and the note left here said they had to be:
   that the viewport's arrows "are lit because they are geometry in a lit
   scene", so any 2D imitation of their roundness would read as grubby. The
   first half of that is simply wrong — scene/gizmo.js builds them from
   MeshBasicMaterial, which is unlit flat colour. What makes a gizmo arrow read
   as an arrow is its silhouette: a cylinder and a cone seen in projection.
   That is not something to imitate in SVG, it is something to render, and
   rendering it costs one small canvas and the meshes the gizmo already
   defines (moveArrowMeshes, shared so the two cannot drift).

   The canvas draws; the SVG on top of it still carries the labels and the
   deliberately-oversized grab areas, and both are laid out from the SAME
   projection the canvas renders with — so a hit area cannot end up somewhere
   its arrow is not. If WebGL will not start, the SVG falls back to drawing the
   flat arrows it always did. */

/* Looking from front-right-above, chosen to put the three axes where a reader
   expects them rather than by taste: +Y always projects straight up (the
   camera's right vector is horizontal, so Y has no horizontal component
   whatever the angle); this direction is the one that additionally lands +X
   about 9° below the horizontal and +Z at 45° down-left. Solving those two
   gives n = (0.339, 0.397, 0.853), which is where this came from. Z ends up
   about half the screen length of X — the foreshortening of an axis pointing
   at you, and the thing that says "this one comes towards you" without a word. */
const EYE = new THREE.Vector3(0.339, 0.397, 0.853);
/* viewBox units per world unit. The gizmo's arrow reaches ~0.965 of a unit, so
   this puts the longest arrow's tip ~38 units out — the reach the flat dial
   had, kept so the widget's size and the labels' room are unchanged. */
const R = 42;

const AX = {
  /* `label` says what the drag DOES, not what the axis is called: "z", and even
     "out", need you to already know the convention, whereas "forwards" can be
     read by someone who has never opened the Viewer readout.

     Every label is centred on something — two on their own arrowheads, the long
     one on the widget — rather than each being anchored a different way, which
     is what made three labels set at three angles read as three unrelated bits
     of text. `to` (the tip) and `dir` (the screen direction a drag is projected
     onto) are computed, not written here: two hand-kept copies of where an
     arrow points is exactly how a hit area drifts off its arrow. */
  z: { col: '#5BA9D6', sign: 1, label: 'backwards / forwards', anchor: 'middle' },
  x: { col: '#FF5C5C', sign: 1, label: 'right / left',         anchor: 'middle' },
  y: { col: '#6BE08A', sign: 1, label: 'up / down',            anchor: 'middle' }
};
/* The scene gizmo says "this is the handle you are about to move" by painting
   the whole handle its highlight yellow (GZC.hi in scene/gizmo.js), and these
   arrows stand for those handles, so they say it the same way — one flat
   colour, exactly as the gizmo does it. */
const HI = '#F2C300';

/* ── the rendered dial ──────────────────────────────────────────────────── */
const AXV = { x: new THREE.Vector3(1,0,0), y: new THREE.Vector3(0,1,0), z: new THREE.Vector3(0,0,1) };
const TIP = 0.965;                 // where the gizmo's cone actually ends
/* The box this all lands inside. The height is not a constant: it is whatever
   the finished drawing turns out to need, set in relayout() once the labels
   have been measured, and the SVG's own height:auto then shrinks the panel to
   match. Picking it in advance is what left a band of empty glass between the
   arrows and the caption — the dial was being centred in a box sized for
   nothing in particular. Width stays fixed: it is what sets how large the
   arrows draw against a panel of fixed width. */
const VB = { x: -14, y: -6, w: 128, h: 116 };
const PAD = 5;              // breathing room inside the box
const CAP_LEAD = 11;        // from the lowest thing in the dial to the caption

/* One camera for both jobs — rendering the arrows, and telling the SVG where
   they ended up. The frustum is set so the world origin falls exactly on C and
   one world unit is R viewBox units, which is what lets a canvas underneath
   and an SVG on top share one coordinate system. */
const dialCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.01, 100);
dialCam.position.copy(EYE).multiplyScalar(8);
dialCam.up.set(0, 1, 0);
dialCam.lookAt(0, 0, 0);
dialCam.updateMatrixWorld();
/** Re-aims the frustum so the world origin lands exactly on C. */
function aimDial(){
  dialCam.left   = -(C.x - VB.x)/R;
  dialCam.right  =  (VB.x + VB.w - C.x)/R;
  dialCam.top    =  (C.y - VB.y)/R;
  dialCam.bottom = -(VB.y + VB.h - C.y)/R;
  dialCam.updateProjectionMatrix();
}

/* The camera's own screen axes, which is all the layout needs: an orthographic
   projection turns a world direction into a screen offset with no perspective
   divide, so one dot product per axis replaces projecting points and is the
   same answer. y is negated because SVG counts downwards. */
const FWD = EYE.clone().normalize().negate();
const RIGHT = new THREE.Vector3().crossVectors(FWD, new THREE.Vector3(0,1,0)).normalize();
const UPV = new THREE.Vector3().crossVectors(RIGHT, FWD);
/** A world direction, as an offset in viewBox units. */
const screenOff = v => [v.dot(RIGHT) * R, -v.dot(UPV) * R];

/* Each label goes where its own arrow leaves room, which is a different place
   for each of them — the one thing here that is a judgement rather than a
   calculation, because it is a question about the other two arrows:

     y points straight up, and nothing else is up there → centred above its head.
     x points right and slightly DOWN, which leaves the wedge above it empty
       (the y arrow is the only other thing up there, and it is far to the
       left) → centred over its head, in that wedge. The gap has to be bigger
       than y's: this label is wide enough to reach back over the arrow's own
       shaft, so it is cleared by more than the arrowhead alone would need.

   Both ride the computed tip, so they follow if the angle ever changes. */
const LBL_GAP = { x: 15, y: 8 };
/* The third label is not hung off its arrow at all. "backwards / forwards" is
   more than half the width of the whole widget, so centring it on a tip that
   sits well left of centre pushes it straight off the panel — which is why it
   used to be the one label anchored from its start, and why it read as the odd
   one out. It is a caption for the widget instead: centred, along the bottom,
   in its own axis's colour, which is what ties it to the blue arrow. */

let dial = null;   // { canvas, renderer, scene, mats } once WebGL is up

function makeDial(){
  const canvas = document.createElement('canvas');
  canvas.className = 'camrig__gl';
  let renderer;
  try{
    /* alpha, so the glass panel behind shows through exactly as it did behind
       the SVG. Antialiased because this is a 120px-wide diagram of three thin
       cones and aliasing is the whole difference between "3D arrow" and
       "jagged triangle". */
    renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
  }catch(_){
    return null;                      // no context — the flat SVG arrows stay
  }
  renderer.setClearAlpha(0);
  const scene = new THREE.Scene();
  const mats = {};
  for(const k of ['x','y','z']){
    const meshes = moveArrowMeshes(k, GIZMO_COLOURS[k]);
    mats[k] = meshes.map(m => m.material);
    meshes.forEach(m => scene.add(m));
  }
  scene.add(moveHubMesh());
  return { canvas, renderer, scene, mats };
}

/* Sized from the SVG rather than from a constant: the rig is a fixed width in
   CSS today, but a canvas that assumes that is a canvas that is wrong the
   first time the panel is made resizable, and the cost of asking is one
   getBoundingClientRect per resize. */
function sizeDial(){
  if(!dial) return;
  const box = dial.canvas.getBoundingClientRect();
  if(!box.width || !box.height) return;            // folded away or not shown yet
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  dial.renderer.setPixelRatio(dpr);
  dial.renderer.setSize(box.width, box.height, false);
  paintDial();
}
function paintDial(){
  if(dial) dial.renderer.render(dial.scene, dialCam);
}

const SHUT_KEY = 'illusio3d.camrig-shut';
const TRI = '<svg viewBox="0 0 10 10"><path d="M2.6 1.4 7.4 5 2.6 8.6z"/></svg>';

const CAM_ICON =
  '<rect x="-9" y="-6.5" width="18" height="13" rx="2.4" fill="none" stroke="currentColor" stroke-width="1.6"/>' +
  '<circle cx="0" cy="0" r="3.6" fill="none" stroke="currentColor" stroke-width="1.6"/>' +
  '<path d="M-4.5 -6.5l1.8-2.6h5.4l1.8 2.6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>';

/* Same glyph the standalone toggle used before it moved in here — see the
   note above gridBtn's build for why it had to move. */
/* Two panels meeting at a seam — which of the two errors it carries. */
const SEAM_ICON = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3">'
  + '<path d="M3 3.6 8 2.4v11.2L3 12.4z"/><path d="M13 3.6 8 2.4v11.2l5-1.2z"/></svg>';

const GRID_ICON = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3">' +
  '<rect x="2" y="2" width="12" height="12" rx="1"/><path d="M2 6.33h12M2 10.67h12M6.33 2v12M10.67 2v12"/></svg>';

let rig = null, gridBtn = null, seamBtn = null, resetBtn = null;
/* Set by build(); run by syncCamRig the first time the widget is actually on
   screen, because it measures text and text has no size until it is rendered. */
let relayout = null, laidOut = false;

function build(){
  if(rig) return rig;
  /* On the STAGE, not inside a view. Every .view is position:absolute inset:0
     and hides itself with visibility:hidden, so a rig parented to #viewSite
     disappears along with it the moment you leave On-site, whatever its own
     classes say — which is half of why this widget was On-site only. The stage
     is the one box that is always showing, it is already position:relative,
     and since every view fills it exactly, one position measured against it is
     correct in all of them. */
  const host = $('#stage');
  if(!host) return null;
  rig = el('div', 'camrig hidden');
  rig.id = 'camRig';

  /* The whole rig folds away, the same gesture and the same triangle the
     outliner's object rows use — it floats over the render, and someone who has
     finished with the viewpoint should be able to get it out of the way without
     leaving the view. Remembered, like every other workspace preference. */
  const head = el('div', 'camrig__head');
  const tri = el('button', 'camrig__tri');
  tri.innerHTML = TRI;
  tri.type = 'button';
  head.appendChild(tri);
  const icon = document.createElement('span');
  icon.className = 'camrig__icon';
  icon.innerHTML = '<svg viewBox="-11 -11 22 22">' + CAM_ICON + '</svg>';
  head.appendChild(icon);
  head.appendChild(el('span', 'camrig__title', 'Calibrate camera'));
  /* The calibration grid's toggle, folded in here rather than floating on its
     own over the canvas: this widget's dial arrows are deliberately drawn
     past its own box (camrig__dial's overflow:visible — the arrowheads have
     to reach past the header to be grabbable at all) and their invisible
     grab-lines are pointer-events:auto so those arrows are draggable from a
     comfortable distance. A separate button sharing that corner sat inside
     one of those grab-lines: it painted fine, and every click landed on the
     "up" arrow's hit area instead and nudged the viewpoint by nothing,
     silently. Inside the widget's own header there is no such collision —
     it is simply another control belonging to the same panel. */
  gridBtn = el('button', 'camrig__gridbtn hidden');
  gridBtn.type = 'button';
  gridBtn.title = 'Calibration grid — a flat, camera-facing reference plane over the shared seam, to check the automatic calibration by eye. It should look flat and square. If it looks distorted, first re-check the camera calibration; if that doesn’t fix it, check the screen dimensions.';
  gridBtn.innerHTML = GRID_ICON;
  gridBtn.onclick = () => {
    S.showCalibGrid = !S.showCalibGrid;
    gridBtn.classList.toggle('on', S.showCalibGrid);
    requestRender();
  };
  head.appendChild(gridBtn);

  /* Which of two errors the corner carries.
     A corner pin is a 4-point homography and the only question is which four.
     Pinned to the MARKS, the second panel's outline sits on the building and
     the disagreement between the marks and the geometry lands at the seam,
     growing with depth. Pinned to where the panel PROJECTS, the seam is exact
     by construction — one mapping, nothing for two to disagree about — and
     the disagreement lands on that panel's outline instead. It cannot be
     removed by pinning, only placed. */
  seamBtn = el('button', 'camrig__gridbtn hidden');
  seamBtn.type = 'button';
  seamBtn.innerHTML = SEAM_ICON;
  head.appendChild(seamBtn);
  seamBtn.onclick = () => {
    S.siteSeamExact = !S.siteSeamExact;
    syncCamRig();
    requestRender();
  };

  rig.appendChild(head);

  let shut = false;
  try{ shut = localStorage.getItem(SHUT_KEY) === '1'; }catch(_){}
  const paintShut = ()=>{
    rig.classList.toggle('camrig--shut', shut);
    tri.classList.toggle('shut', shut);
    tri.title = shut ? 'Show the viewpoint arrows' : 'Fold the viewpoint arrows away';
    tri.setAttribute('aria-expanded', shut ? 'false' : 'true');
  };
  tri.onclick = ()=>{
    shut = !shut;
    try{ localStorage.setItem(SHUT_KEY, shut ? '1' : '0'); }catch(_){}
    paintShut();
  };
  paintShut();

  /* Provisional, so the elements can be built at all. relayout() replaces
     every one of these numbers as soon as the widget is on screen and the
     labels can be measured. */
  for(const k of ['x','y','z']){
    const a = AX[k];
    a.off = screenOff(AXV[k].clone().multiplyScalar(TIP));
    const len = Math.hypot(a.off[0], a.off[1]) || 1;
    a.dir = [a.off[0] / len, a.off[1] / len];
    a.to = [C.x + a.off[0], C.y + a.off[1]];
    a.at = a.to.slice();
  }

  const wrap = el('div', 'camrig__dialwrap');
  dial = makeDial();
  if(dial) wrap.appendChild(dial.canvas);

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  /* Wider and taller than the 100-unit dial, because the labels sit OUTSIDE the
     arrows: the box has to carry them or they are clipped away at exactly the
     sizes the rig is actually rendered at. */
  svg.setAttribute('viewBox', `${VB.x} ${VB.y} ${VB.w} ${VB.h}`);
  svg.setAttribute('class', 'camrig__dial');
  const mk = (tag, attrs) => {
    const n = document.createElementNS('http://www.w3.org/2000/svg', tag);
    for(const k in attrs) n.setAttribute(k, attrs[k]);
    return n;
  };
  const parts = {};
  for(const k of ['z','x','y']){
    const a = AX[k];
    const g = mk('g', { class: 'camrig__ax', 'data-ax': k });
    const [dx, dy] = a.dir, px = -dy, py = dx;
    const t = 9, wdt = 4.6;                        // head length and half-width
    const bx = a.to[0] - dx*t, by = a.to[1] - dy*t;   // where the head meets the shaft
    /* THE FALLBACK DRAWING. The canvas behind is what is normally seen; these
       two are what is left if WebGL would not start, which is the one case
       where a flat arrow beats no arrow. Built either way and simply not shown
       when the canvas is doing its job — the alternative is a second code path
       that only ever runs on machines nobody develops on. */
    const shaft = mk('line', { x1:C.x, y1:C.y, x2:bx, y2:by, stroke:a.col,
                               'stroke-width':4.6, 'stroke-linecap':'round' });
    g.appendChild(shaft);
    const head = mk('polygon', { fill:a.col, points:
      `${a.to[0]},${a.to[1]} ` +
      `${bx+px*wdt},${by+py*wdt} ` +
      `${bx-px*wdt},${by-py*wdt}` });
    g.appendChild(head);
    if(dial){ shaft.setAttribute('visibility', 'hidden'); head.setAttribute('visibility', 'hidden'); }
    /* Which way each arrow actually moves you, in the words someone standing in
       front of the building would use. Three arrows say "three axes" but not
       which is which, and this rig exists to be used without first learning
       that. Painted in the axis's own colour so the label and its arrow read as
       one thing even where the label has to sit well clear of it. */
    const lbl = mk('text', { x:a.at[0], y:a.at[1], fill:a.col,
                             'text-anchor':a.anchor, class:'camrig__lbl' });
    lbl.textContent = a.label;
    g.appendChild(lbl);
    /* Held on the group rather than looked up by selector at paint time: the
       same shape scene/gizmo.js's userData.visuals uses, and for the same
       reason — the highlight runs on pointermove. */
    g.paint = on => {
      const c = on ? HI : a.col;
      shaft.setAttribute('stroke', c);
      head.setAttribute('fill', c);
      lbl.setAttribute('fill', c);
      g.classList.toggle('lit', on);
      /* The rendered arrow says it the way the gizmo says it — the same
         GZC.hi yellow on the actual material, not a glow faked over the top. */
      if(dial){
        for(const m of dial.mats[k]) m.color.set(on ? HI : GIZMO_COLOURS[k]);
        paintDial();
      }
    };
    /* The whole point of this widget: a hit area far wider than the arrow it
       covers, so every axis is grabbable without aiming. Same reasoning as
       every gizmo handle's invisible picker in scene/gizmo.js. */
    const grab = mk('line', { x1:C.x, y1:C.y, x2:a.to[0], y2:a.to[1],
                              stroke:'transparent', 'stroke-width':22,
                              'stroke-linecap':'round', class:'camrig__grab' });
    g.appendChild(grab);
    parts[k] = { shaft, head, lbl, grab };
    svg.appendChild(g);
  }
  /* The hub, in the gizmo's own centre colour (GZC.all). Only when the canvas
     is not drawing the gizmo's real one behind it. */
  const hub = dial ? null : mk('circle', { cx:C.x, cy:C.y, r:5.4, fill:'#E8EDF1' });
  if(hub) svg.appendChild(hub);
  wrap.appendChild(svg);
  rig.appendChild(wrap);

  /* The canvas takes its size from the SVG beside it, which is the element
     with the intrinsic aspect ratio — so this fires when the rig is first
     shown (it has no size while display:none), when it is unfolded, and on
     any zoom or layout change, and none of those need their own hook. */
  if(dial && typeof ResizeObserver !== 'undefined'){
    new ResizeObserver(sizeDial).observe(svg);
  }

  /* CENTRE THE ARROWS, and let the words arrange themselves around them.
     Centring the whole composition instead — arrows AND labels as one
     rectangle — is defensible on paper and wrong to look at: the labels are
     much wider than the arrows, so the drawing gets shoved to one side to make
     the text balance, and the thing your eye actually reads as "the widget"
     ends up off-centre. So the three arrows are centred on the panel, and each
     label is then placed against its own arrow and clamped to stay inside.

     The labels are MEASURED rather than estimated because the one thing here
     not known from the geometry is how wide the text is, which depends on the
     font the platform actually resolved. getBBox needs the element rendered,
     so this runs on first show. */
  relayout = () => {
    const box = {}, sized = k => {
      try{ const b = parts[k].lbl.getBBox(); return { w: b.width, h: b.height }; }
      catch(_){ return null; }                  // not rendered yet
    };
    for(const k of ['x','y','z']){
      box[k] = sized(k);
      if(!box[k] || !box[k].w) return false;    // still hidden — try again later
    }

    /* THE ARROWS ALONE decide the centre: the meeting point and the three
       heads, nothing else. */
    const ox = [0, ...['x','y','z'].map(k => AX[k].off[0])];
    C = { x: VB.x + VB.w/2 - (Math.min(...ox) + Math.max(...ox))/2, y: 0 };

    /* Then how tall the result has to be, measured from the meeting point:
       the y label reaches highest, and the lowest is whichever of the z arrow
       and the x label (now UNDER its own arrowhead) hangs further down. */
    const top = Math.min(AX.y.off[1] - LBL_GAP.y - box.y.h,
                         AX.x.off[1] - LBL_GAP.x - box.x.h);
    const bottom = Math.max(AX.z.off[1], AX.x.off[1]);
    VB.h = PAD + (bottom - top) + CAP_LEAD + box.z.h + PAD;
    svg.setAttribute('viewBox', `${VB.x} ${VB.y} ${VB.w} ${VB.h}`);
    C.y = VB.y + PAD - top;
    /* A baseline, not a top edge: a descender ("backwards / forwards" has two)
       hangs below it, so the last quarter of the cap height is left for them. */
    const capY = VB.y + VB.h - PAD - box.z.h * 0.25;

    for(const k of ['x','y','z']){
      const a = AX[k], p = parts[k];
      a.to = [C.x + a.off[0], C.y + a.off[1]];
      a.at = k === 'z' ? [VB.x + VB.w/2, capY]
           : k === 'y' ? [a.to[0], a.to[1] - LBL_GAP.y]
           /* Over the arrowhead, in the empty wedge above the arrow. Clamped
              so a platform whose font sets this wider cannot push the end of
              the word off the panel. */
           : [Math.min(a.to[0], VB.x + VB.w - PAD - box.x.w/2), a.to[1] - LBL_GAP.x];
      p.lbl.setAttribute('x', a.at[0]);
      p.lbl.setAttribute('y', a.at[1]);
      p.grab.setAttribute('x1', C.x); p.grab.setAttribute('y1', C.y);
      p.grab.setAttribute('x2', a.to[0]); p.grab.setAttribute('y2', a.to[1]);
      /* The flat fallback arrows, which are only ever seen when there is no
         canvas — moved anyway, because "correct only on the machines that can
         render" is how a fallback rots. */
      const [dx, dy] = a.dir, px = -dy, py = dx, t = 9, wdt = 4.6;
      const bx = a.to[0] - dx*t, by = a.to[1] - dy*t;
      p.shaft.setAttribute('x1', C.x); p.shaft.setAttribute('y1', C.y);
      p.shaft.setAttribute('x2', bx);  p.shaft.setAttribute('y2', by);
      p.head.setAttribute('points',
        `${a.to[0]},${a.to[1]} ${bx+px*wdt},${by+py*wdt} ${bx-px*wdt},${by-py*wdt}`);
    }
    if(hub){ hub.setAttribute('cx', C.x); hub.setAttribute('cy', C.y); }
    aimDial();
    paintDial();
    return true;
  };

  const foot = el('div', 'camrig__foot');
  /* Only meaningful where there IS a solved point to go back to. On a hand-set
     project the viewpoint is a camera object and this would zero a nudge that
     is not being read — syncCamRig hides it there rather than offering a
     button that does nothing. */
  resetBtn = el('button', 'btn btn--sm btn--gh btn--w', 'Back to the solved point');
  resetBtn.onclick = ()=>{ S.nudge.x = S.nudge.y = S.nudge.z = 0; changed(); };
  foot.appendChild(resetBtn);
  rig.appendChild(foot);

  svg.addEventListener('pointerdown', onDown);
  /* Hover is on the SVG, not per-arrow: the grab areas are deliberately far
     wider than the arrows and therefore overlap, so `closest` on one moving
     pointer picks exactly one — whereas three independent enter/leave pairs
     would leave two arrows lit wherever they cross. */
  svg.addEventListener('pointermove', e => { if(!dragging) setLit(e.target.closest('.camrig__ax')); });
  svg.addEventListener('pointerleave', () => { if(!dragging) setLit(null); });
  host.appendChild(rig);
  return rig;
}

/* Exactly scene/gizmo.js's setHover: one arrow lit at a time, repainted only
   when it actually changes, so a pointermove over the same arrow costs nothing. */
let lit = null, dragging = false;
function setLit(g){
  if(lit === g) return;
  if(lit) lit.paint(false);
  lit = g;
  if(lit) lit.paint(true);
}

function changed(){
  requestRender();
  /* The Viewer readout in Master Settings is text, not a bound input, so it
     cannot be refreshed in place — rebuilt on release rather than per move,
     which would tear down the panel under the pointer sixty times a second. */
  emit('eye:nudged');
}

function onDown(ev){
  const g = ev.target.closest('.camrig__ax');
  if(!g || ev.button !== 0) return;
  ev.preventDefault(); ev.stopPropagation();
  const k = g.dataset.ax, a = AX[k];
  g.classList.add('on');
  dragging = true; setLit(g);
  const svg = ev.currentTarget;
  try{ svg.setPointerCapture(ev.pointerId); }catch(_){}

  /* WHAT THE ARROWS ACTUALLY MOVE, which is not the same thing on every
     project. eyeVec() (scene/helpers.js) reads the viewpoint off a visible
     camera OBJECT when there is one and ignores S.nudge entirely — that is the
     hand-set and video-wall path, where the camera is a thing you drag. Writing
     the nudge there would move nothing at all, silently, which is exactly the
     kind of dead control this rig exists to replace. So the arrows drive
     whichever of the two the app is really reading, decided once at
     pointerdown rather than per move. */
  const cam = cameraObj();
  let lastX = ev.clientX, lastY = ev.clientY;
  const move = e => {
    /* Metres per pixel, scaled by how far the viewer stands off — a fixed rate
       is unusably coarse at 30 m and unusably fine at 3. Shift coarsens and Alt
       refines by ten, the convention every scrub handle in controls.js and every
       calibrate crosshair already uses. */
    const dist = Math.max(1, Math.abs(eyeVec().z));
    /* Tuned so a comfortable drag is a correction, not a relocation: at 10 m
       out this is ~12 mm per pixel, so dragging the width of the widget moves
       the viewer about a metre. The first pass was three times this and a
       60 px flick jumped 2.19 m, which is not a nudge. */
    const rate = dist * 0.0012 * (e.shiftKey ? 10 : e.altKey ? .1 : 1);
    const dx = e.clientX - lastX, dy = e.clientY - lastY;
    lastX = e.clientX; lastY = e.clientY;
    // project the movement onto this arrow's own screen direction
    const along = (dx * a.dir[0] + dy * a.dir[1]) * a.sign * rate;
    if(cam){ cam.pos[k] += along; syncTransform(cam); rebuildHelpers(); }
    else S.nudge[k] += along;
    requestRender();
  };
  const up = e => {
    g.classList.remove('on');
    /* Asked of the page rather than of the event: the pointer is captured for
       the duration of the drag, so every event's target is the SVG itself and
       "am I still over this arrow?" cannot be read off it. Without this the
       arrow you just finished dragging goes dark under your own cursor. */
    dragging = false;
    const at = e && e.clientX != null ? document.elementFromPoint(e.clientX, e.clientY) : null;
    setLit(at && at.closest ? at.closest('.camrig__ax') : null);
    svg.removeEventListener('pointermove', move);
    svg.removeEventListener('pointerup', up);
    svg.removeEventListener('pointercancel', up);
    changed();
    /* Moving a camera OBJECT edits the document, so it joins the undo stack
       exactly as dragging that same camera in the viewport does. A nudge does
       not: S.nudge is framing rather than content, and history.js leaves it
       out on purpose. */
    if(cam) pushHistory();
  };
  svg.addEventListener('pointermove', move);
  svg.addEventListener('pointerup', up);
  svg.addEventListener('pointercancel', up);
}

/* EVERY VIEW, once there is a viewpoint to move.
   It was On-site only, on the reasoning that Orbit already has a draggable eye
   helper and Flat has no viewpoint to see. Both are true and neither settles
   it: the viewpoint is ONE thing wherever you happen to be looking from, and
   having to remember which tab owns the control is worse than a widget that is
   always in the same corner. Flat earns it too — the deliverable's distortion
   IS the viewpoint, so moving the viewer while watching the stretch is a
   reasonable thing to want.
   Not in Calibrate, which is where the viewpoint is being established rather
   than adjusted, and which hides the whole workspace anyway. */
export function syncCamRig(){
  const r = build();
  if(!r) return;
  const show = S.solved && S.stage !== 'calib';
  r.classList.toggle('hidden', !show);
  /* Once, on the first frame it is visible. getBBox on a display:none element
     answers zero (or throws), so a layout worked out at build time would be a
     layout worked out from nothing — which is why this waits rather than
     running beside the rest of the building. */
  if(show && !laidOut && relayout){
    laidOut = relayout();
    /* A webfont arriving after first paint re-measures every label. Cheap to
       ask for, and the alternative is a rig laid out around the fallback font
       for the rest of the session. */
    if(laidOut && document.fonts && document.fonts.ready)
      document.fonts.ready.then(() => { if(relayout) relayout(); }).catch(() => {});
  }
  if(resetBtn) resetBtn.classList.toggle('hidden', !!cameraObj());
  if(gridBtn){
    gridBtn.classList.toggle('hidden', !S.cornerPanel);
    gridBtn.classList.toggle('on', !!S.showCalibGrid);
  }
  if(seamBtn){
    seamBtn.classList.toggle('hidden', !S.cornerPanel);
    seamBtn.classList.toggle('on', S.siteSeamExact === true);
    seamBtn.title = S.siteSeamExact === true
      ? 'Seam closed: the second panel is pinned where it projects, so the two meet exactly. Its outline may sit off the marked edge. Click to pin it to the marks instead.'
      : 'Pinned to the marks: the second panel’s outline sits on the marked edge, and any disagreement shows at the seam. Click to close the seam instead.';
  }
  if(!show) return;
  /* Sits UNDER whatever already floats in that corner rather than at a fixed
     offset: the view tabs are one line, but the hint below them wraps to two
     or three depending on the numbers in it, and a guessed top put the rig's
     own title behind it. Measured against the view's own box, so it follows
     whatever those elements actually do. */
  const host = $('#stage'), tabs = $('#viewtabs'), hint = $('#hint');
  if(!host) return;
  const top = host.getBoundingClientRect().top;
  const under = elm => (!elm || elm.classList.contains('hidden'))
    ? 0 : elm.getBoundingClientRect().bottom - top;
  r.style.top = Math.round(Math.max(under(tabs), under(hint)) + 10) + 'px';
}
on('stage:fit', syncCamRig);
